import { useState } from 'react';
import DataTable from '../components/DataTable';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import { SelectField, TextArea, TextField } from '../components/FormField';
import useSharePointList from '../hooks/useSharePointList';
import { createReport, listReports } from '../services/reports';
import { exportToExcel } from '../utils/excelExport';
import { formatDate } from '../utils/formatters';
import useAuth from '../auth/useAuth';

/**
 * Daily field reports. Engineers submit; managers review. Filters are simple
 * client-side search for now — a future pass can push filters into Graph
 * $filter once we know how ops actually want to slice.
 */
export default function Reports() {
  const { name, email } = useAuth();
  const { data, loading, refresh } = useSharePointList(listReports);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    ReportDate: new Date().toISOString().slice(0, 10),
    PlantName: '',
    ProgramName: '',
    Shift: 'Day',
    Summary: '',
    IssuesFound: '',
    ActionsTaken: '',
  });

  const submit = async () => {
    setSaving(true);
    try {
      await createReport({
        ...draft,
        Title: `${draft.ProgramName || 'Report'} — ${draft.ReportDate}`,
        SubmittedBy: name,
        SubmittedByEmail: email,
        SubmittedAt: new Date().toISOString(),
      });
      setOpen(false);
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'ReportDate',
      label: 'Date',
      render: (r) => formatDate(r.ReportDate),
    },
    { key: 'PlantName', label: 'Plant' },
    { key: 'ProgramName', label: 'Program' },
    { key: 'Shift', label: 'Shift' },
    { key: 'SubmittedBy', label: 'Submitted by' },
    {
      key: 'Summary',
      label: 'Summary',
      render: (r) => <span style={{ color: 'var(--brand-muted)' }}>{r.Summary}</span>,
    },
  ];

  const exportBtn = (
    <button
      className="btn btn-outline btn-sm"
      onClick={() =>
        exportToExcel(data, {
          filename: 'daily-reports.xlsx',
          sheetName: 'Reports',
          columns: [
            { key: 'ReportDate', label: 'Date' },
            { key: 'PlantName', label: 'Plant' },
            { key: 'ProgramName', label: 'Program' },
            { key: 'Shift', label: 'Shift' },
            { key: 'SubmittedBy', label: 'Submitted by' },
            { key: 'Summary', label: 'Summary' },
            { key: 'IssuesFound', label: 'Issues' },
            { key: 'ActionsTaken', label: 'Actions' },
          ],
        })
      }
    >
      Export to Excel
    </button>
  );

  return (
    <>
      <div className="page-header">
        <h1>Reports</h1>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setOpen(true)}>
            + Submit Report
          </button>
        </div>
      </div>

      {loading && <Loading />}
      {!loading && (
        <DataTable rows={data} columns={columns} exportButton={exportBtn} />
      )}

      <Modal
        open={open}
        onClose={() => !saving && setOpen(false)}
        title="Submit Daily Report"
      >
        <div className="form-grid">
          <TextField
            label="Date"
            type="date"
            value={draft.ReportDate}
            onChange={(v) => setDraft((d) => ({ ...d, ReportDate: v }))}
          />
          <SelectField
            label="Shift"
            value={draft.Shift}
            onChange={(v) => setDraft((d) => ({ ...d, Shift: v }))}
            options={['Day', 'Afternoon', 'Night']}
          />
          <TextField
            label="Plant"
            value={draft.PlantName}
            onChange={(v) => setDraft((d) => ({ ...d, PlantName: v }))}
          />
          <TextField
            label="Program"
            value={draft.ProgramName}
            onChange={(v) => setDraft((d) => ({ ...d, ProgramName: v }))}
          />
          <TextArea
            label="Summary"
            value={draft.Summary}
            onChange={(v) => setDraft((d) => ({ ...d, Summary: v }))}
          />
          <TextArea
            label="Issues Found"
            value={draft.IssuesFound}
            onChange={(v) => setDraft((d) => ({ ...d, IssuesFound: v }))}
          />
          <TextArea
            label="Actions Taken"
            value={draft.ActionsTaken}
            onChange={(v) => setDraft((d) => ({ ...d, ActionsTaken: v }))}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-outline" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </Modal>
    </>
  );
}
